import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import {
  FETCH_ERROR_MESSAGE_TICKETS,
  CLOSE_ERROR_MESSAGE_TICKETS,
} from '../../utils/constants';
import { TicketRecord } from '../../Interfaces/Tickets/TicketRecord';
import { TicketCloseInfo } from '../../Interfaces/Tickets/TicketCloseInfo';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { initialTicketFilters } from '../../store/UserSettings/UserSettingsState';

export enum TicketsActionTypes {
  RESET_STATE = 'RESET_STATE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  RECEIVED_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_HTTP_RESPONSE_ERROR_CODE',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  REQUEST_TICKETS = 'REQUEST_TICKETS',
  RECEIVED_TICKETS = 'RECEIVED_TICKETS',
  TICKET_CLOSED = 'TICKET_CLOSED',
  FAILED_TO_CLOSE_TICKET = 'FAILED_TO_CLOSE_TICKET',
  TICKET_NOTIFICATION_SUCCESS_MESSAGE = 'TICKET_NOTIFICATION_SUCCESS_MESSAGE',
  RECEIVED_TICKET_STORE_LIST = 'RECEIVED_TICKET_STORE_LIST',
  RECEIVED_TICKET_DIVISION_LIST = 'RECEIVED_TICKET_DIVISION_LIST',
  RECEIVED_TICKET_NODETYPE_LIST = 'RECEIVED_TICKET_NODETYPE_LIST',
  RECEIVED_TICKET_LANE_NUMBER_LIST = 'RECEIVED_TICKET_LANE_NUMBER_LIST',
  RECEIVED_TICKET_CLUSTER_LIST = 'RECEIVED_TICKET_CLUSTER_LIST',
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface TicketsState {
  unauthorizedRequest: boolean;
  fetchError: FetchError;
  isFetchingTickets: boolean;
  tickets: TicketRecord[];
  notificationSuccessMessage: string;
  siteNumbers: string[];
  assetCategories: string[];
  divisions: string[];
  nodeTypes: string[];
  clusters: string[];
  laneNumbers: string[];
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface ResetStateAction {
  type: TicketsActionTypes.RESET_STATE;
}

interface UnauthorizedRequestAction {
  type: TicketsActionTypes.UNAUTHORIZED_REQUEST;
}

interface ReceivedErrorCode {
  type: TicketsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: TicketsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

interface RequestTicketsAction {
  type: TicketsActionTypes.REQUEST_TICKETS;
}

interface ReceivedTicketsAction {
  type: TicketsActionTypes.RECEIVED_TICKETS;
  data: TicketRecord[];
}

interface TicketClosedAction {
  type: TicketsActionTypes.TICKET_CLOSED;
  ticketId: number;
}

interface FailedToCloseTicketAction {
  type: TicketsActionTypes.FAILED_TO_CLOSE_TICKET;
  statusCode: number;
}

interface NotificationSuccessMessage {
  type: TicketsActionTypes.TICKET_NOTIFICATION_SUCCESS_MESSAGE;
  message: string;
}

interface GetTicketStoreListAction {
  type: TicketsActionTypes.RECEIVED_TICKET_STORE_LIST;
  siteNumbers: string[];
}

interface GetTicketDevisionListAction {
  type: TicketsActionTypes.RECEIVED_TICKET_DIVISION_LIST;
  divisions: string[];
}

interface GetTicketNodeTypeListAction {
  type: TicketsActionTypes.RECEIVED_TICKET_NODETYPE_LIST;
  nodeTypes: string[];
}

interface GetTicketLaneNumberListAction {
  type: TicketsActionTypes.RECEIVED_TICKET_LANE_NUMBER_LIST;
  laneNumbers: string[];
}

interface GetTicketClusterListAction {
  type: TicketsActionTypes.RECEIVED_TICKET_CLUSTER_LIST;
  clusters: string[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ResetStateAction
  | UnauthorizedRequestAction
  | RequestTicketsAction
  | ReceivedTicketsAction
  | GetTicketLaneNumberListAction
  | GetTicketClusterListAction
  | GetTicketDevisionListAction
  | GetTicketNodeTypeListAction
  | TicketClosedAction
  | FailedToCloseTicketAction
  | ReceivedErrorCode
  | GetTicketStoreListAction
  | NotificationSuccessMessage
  | ResetHttpResponseError;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (
  response: Response,
  dispatch: any,
  isClosingTicket: boolean = false
): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: TicketsActionTypes.UNAUTHORIZED_REQUEST,
    });
  } else if (isClosingTicket) {
    dispatch({
      type: TicketsActionTypes.FAILED_TO_CLOSE_TICKET,
      statusCode: response.status,
    });
  } else {
    dispatch({
      type: TicketsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(`Dispatching ${TicketsActionTypes.RESET_STATE}`);

    dispatch({
      type: TicketsActionTypes.RESET_STATE,
    });
  },

  resetTicketsError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${TicketsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`
      );

      dispatch({
        type: TicketsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  fetchTickets:
    (getClosed: boolean, noFilter: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${TicketsActionTypes.REQUEST_TICKETS}`);
      dispatch({
        type: TicketsActionTypes.REQUEST_TICKETS,
      });
      
      var nodeTicketsInfo = appState.userSettingsReducer.ticketFilter;

      if(noFilter)
        nodeTicketsInfo = initialTicketFilters;

      const response = await fetch(
        `tickets/GetAll/${getClosed}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          nodeTicketsInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as TicketRecord[];

      console.log(`Dispatching ${TicketsActionTypes.RECEIVED_TICKETS}`);

      dispatch({
        type: TicketsActionTypes.RECEIVED_TICKETS,
        data: result,
      });
    },

  sendCloseTicket:
    (
      ticketId: number,
      ticketCloseInfo: TicketCloseInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching Close Ticket`);

      const response = await fetch(
        `tickets/Close/${ticketId}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          ticketCloseInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch, true)) return;

      console.log(`Ticket closed: ${ticketId}`);

      dispatch({
        type: TicketsActionTypes.TICKET_CLOSED,
        ticketId: ticketId,
      });

      dispatch({
        type: TicketsActionTypes.TICKET_NOTIFICATION_SUCCESS_MESSAGE,
        message: 'CLOSE_TICKET_SENT_SUCCESS',
      });
    },

  clearCloseTicketNotificationSuccessMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${TicketsActionTypes.TICKET_NOTIFICATION_SUCCESS_MESSAGE} from TicketsState`
      );

      dispatch({
        type: TicketsActionTypes.TICKET_NOTIFICATION_SUCCESS_MESSAGE,
        message: '',
      });
    },

  getTicketStoreList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetTicketSiteNumbers`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${TicketsActionTypes.RECEIVED_TICKET_STORE_LIST}`
      );

      dispatch({
        type: TicketsActionTypes.RECEIVED_TICKET_STORE_LIST,
        siteNumbers: result,
      });
    },

  getTicketDivisionList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetTicketDivisions`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${TicketsActionTypes.RECEIVED_TICKET_DIVISION_LIST}`
      );

      dispatch({
        type: TicketsActionTypes.RECEIVED_TICKET_DIVISION_LIST,
        divisions: result,
      });
    },

  getTicketNodeTypeList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetTicketNodeTypes`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${TicketsActionTypes.RECEIVED_TICKET_NODETYPE_LIST}`
      );

      dispatch({
        type: TicketsActionTypes.RECEIVED_TICKET_NODETYPE_LIST,
        nodeTypes: result,
      });
    },

  getTicketLaneNumbers:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetTicketLaneNumbers`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${TicketsActionTypes.RECEIVED_TICKET_LANE_NUMBER_LIST}`
      );

      dispatch({
        type: TicketsActionTypes.RECEIVED_TICKET_LANE_NUMBER_LIST,
        laneNumbers: result,
      });
    },

  getTicketNodeGroups:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetTicketNodeGroups`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${TicketsActionTypes.RECEIVED_TICKET_CLUSTER_LIST}`
      );

      dispatch({
        type: TicketsActionTypes.RECEIVED_TICKET_CLUSTER_LIST,
        clusters: result,
      });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: TicketsState = {
  unauthorizedRequest: false,
  isFetchingTickets: false,
  fetchError: { StatusCode: 0, Message: '' },
  tickets: [],
  notificationSuccessMessage: '',
  siteNumbers: [],
  assetCategories: [],
  divisions: [],
  nodeTypes: [],
  clusters: [],
  laneNumbers: [],
};

export const reducer: Reducer<TicketsState> = (
  state: TicketsState | undefined,
  incomingAction: Action
): TicketsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case TicketsActionTypes.RESET_STATE:
      return unloadedState;

    case TicketsActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };

    case TicketsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_TICKETS',
      };
      return {
        ...state,
        isFetchingTickets: false,
        fetchError: fetchErrorData,
      };

    case TicketsActionTypes.FAILED_TO_CLOSE_TICKET:
      let closeErrorData = {
        StatusCode: action.statusCode,
        Message: 'CLOSE_ERROR_MESSAGE_TICKETS',
      };
      return {
        ...state,
        isFetchingTickets: false,
        fetchError: closeErrorData,
      };

    case TicketsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };

    case TicketsActionTypes.REQUEST_TICKETS:
      return {
        ...state,
        tickets: [],
        isFetchingTickets: true,
      };

    case TicketsActionTypes.RECEIVED_TICKETS:
      return {
        ...state,
        isFetchingTickets: false,
        tickets: action.data,
      };

    case TicketsActionTypes.TICKET_CLOSED:
      return {
        ...state,
        tickets: [...state.tickets].map(ticket => {
          if (ticket.IncidentID == action.ticketId) {
            return { ...ticket, Status: ApiEnums.IncidentStatus.Closed };
          } else {
            return ticket;
          }
        }),
      };

    case TicketsActionTypes.TICKET_NOTIFICATION_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationSuccessMessage: action.message,
      };
    case TicketsActionTypes.RECEIVED_TICKET_STORE_LIST:
      return {
        ...state,
        siteNumbers: action.siteNumbers,
      };
    case TicketsActionTypes.RECEIVED_TICKET_DIVISION_LIST:
      return {
        ...state,
        divisions: action.divisions,
      };
    case TicketsActionTypes.RECEIVED_TICKET_NODETYPE_LIST:
      return {
        ...state,
        nodeTypes: action.nodeTypes,
      };
    case TicketsActionTypes.RECEIVED_TICKET_LANE_NUMBER_LIST:
      return {
        ...state,
        laneNumbers: action.laneNumbers,
      };
    case TicketsActionTypes.RECEIVED_TICKET_CLUSTER_LIST:
      return {
        ...state,
        clusters: action.clusters,
      };

    default:
      break;
  }
  return state;
};
